import CandidateJobSection from '@/components/CandidateJobSection';
import HeaderTabSelector from '@/components/HeaderTabSelector';
import Sidebar from '@/components/Sidebar';
import StatusTag from '@/components/StatusTag';
import { trioUITheme } from '@/styles/theme';
import { Job, PersonType } from '@/types/api';
import * as Styles from './CandidateJobs.styles';

const FILTERS = ['All', 'Open', 'Closed'] as const;
export type Filter = (typeof FILTERS)[number];

type CandidateJobsComponentProps = {
  onScrollTo: (elementId: string) => void;
  jobs?: Job[];
  jobSearchItems?: Job[];
  filter?: Filter;
  onChangeFilter?: (value: Filter) => void;
  onAssociateJob: (job: Job) => void;
  jobsSearchInput?: string;
  onJobsSearchChange?: (value: string) => void;
  showSearch?: boolean;
  onShowSearchChange?: (value: boolean) => void;
  isSearchLoading?: boolean;
  candidate?: PersonType;
  onDisassociateJob?: (jobId: string) => void;
  disassociatingFromJobMap?: Record<string, boolean>;
};

const CandidateJobsComponent = function ({
  onScrollTo,
  jobs = [],
  filter = 'All',
  onChangeFilter,
  jobSearchItems = [],
  onAssociateJob,
  jobsSearchInput = '',
  onJobsSearchChange,
  showSearch = false,
  onShowSearchChange,
  isSearchLoading = false,
  candidate,
  onDisassociateJob,
  disassociatingFromJobMap = {},
}: CandidateJobsComponentProps): JSX.Element {
  const renderJobSearchItem = (job: Job): JSX.Element => (
    <div className="tw-p-3 tw-flex tw-justify-start tw-gap-4 tw-items-center">
      <span className="tw-text-sm tw-flex tw-gap-2">
        <span className="tw-font-semibold tw-text-primary-60">{job.title}</span>
        <span className="tw-text-neutral-100">at</span>
        <span className="tw-text-neutral-100 tw-font-semibold">{job.account?.name || '-'}</span>
      </span>
      <StatusTag
        className="tw-flex"
        outlined
        status={job.status === 'Active' ? 'Open' : job.status}
        statusColor={trioUITheme.colors['purple-50']}
      />
    </div>
  );

  return (
    <div data-testid="candidate-jobs-page">
      <Styles.FullWidth className="tw-pb-4 tw-border-b tw-mb-4 tw-flex tw-justify-between tw-items-center">
        <HeaderTabSelector options={[...FILTERS]} value={filter} onChange={onChangeFilter} />
        <Styles.AssociateToJob
          buttonLabel="Associate to a job"
          itemButtonLabel="Associate talent to this job"
          items={jobSearchItems}
          onResultClick={onAssociateJob}
          renderItem={renderJobSearchItem}
          keyExtractor={(job) => `${job.id}`}
          searchValue={jobsSearchInput}
          onSearchChange={onJobsSearchChange}
          showInput={showSearch}
          onShowInputChange={onShowSearchChange}
          loading={isSearchLoading}
        />
      </Styles.FullWidth>
      <Styles.PageGrid>
        <Sidebar
          testId="candidate-sidebar"
          sidebarMenus={jobs.map((job) => ({
            label: job.title,
            id: job.id,
            onClick() {
              onScrollTo(job.id);
            },
          }))}
        />
        <Styles.Content>
          {jobs.map((job) => (
            <CandidateJobSection
              key={job.id}
              job={job}
              candidate={candidate}
              disabled={disassociatingFromJobMap[job.id]}
              onDisassociateFromJobClick={() => {
                onDisassociateJob?.(job.id);
              }}
            />
          ))}
        </Styles.Content>
      </Styles.PageGrid>
    </div>
  );
};

export default CandidateJobsComponent;
